import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Button, Color, Flex, Scroller, Search, Spinner } from "src/elements";
import { AnchorLayer } from "src/elements/AnchorLayer/AnchorLayer";
import { useAnchorLayer } from "src/elements/AnchorLayer/store/hooks";
import { Option } from "src/elements/Select/components/Option";
import { useApi } from "src/api";
import { useAsyncState } from "src/hooks/useAsyncState";
import { useDebounce } from "src/hooks/useDebounce";
import { useTranslation } from "src/i18n";
import { PreviousAssortmentCandidate } from "@CommonApi/merchflow/store/pog/_storePogId_/previous-assortment-candidates";
import { routeToUrl } from "@CommonUtils/routes";
import { pages } from "src/utils/pages";

const DropdownElement = styled(Flex)`
  flex-direction: column;
  background-color: ${Color.white};
  border: 1px solid ${Color.primary};
  border-radius: 3px;
  overflow: hidden;
  margin-top: 5px;

  input {
    border: none;
  }
`;

const DropdownDivider = styled(Flex)`
  height: 1px;
  width: calc(100% - 20px);
  background-color: ${Color.lightGray};
  margin: 0 10px;
`;

interface Props {
  storePogId: number;
  isDisabled: boolean;
}

export const PreviewPdfSelector = ({ storePogId, isDisabled }: Props) => {
  const { getPreviousAssortmentCandidatesApi } = useApi();
  const { t } = useTranslation("planogramDelivery");
  const refTrigger = useRef<HTMLDivElement>(null);
  const { refLayer, isRendering, layerState, hideAnchorLayer } = useAnchorLayer({
    refTrigger,
    isUsingHover: true,
  });
  const isOpen = layerState !== null;

  const [defaultCandidates, setDefaultCandidates] =
    useAsyncState<PreviousAssortmentCandidate[]>(undefined);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [searchResults, setSearchResults] = useAsyncState<PreviousAssortmentCandidate[]>(undefined);

  // storePogId changing means a different pog's candidates entirely — reset both so a stale
  // candidate from the previous pog can never be picked (which would open a comparison PDF for
  // the wrong pog), and drop out-of-order responses via the isCurrent flag rather than the
  // "current" fetch racing an older, slower one for a since-navigated-away-from pog.
  useEffect(() => {
    setSearch("");
    setDefaultCandidates(undefined);
    setSearchResults(undefined);
    let isCurrent = true;

    getPreviousAssortmentCandidatesApi(storePogId)
      .then((response) => response?.candidates ?? [])
      .catch(() => [])
      .then((candidates) => {
        if (isCurrent) setDefaultCandidates(candidates);
      });

    return () => {
      isCurrent = false;
    };
  }, [storePogId]);

  useEffect(() => {
    if (!debouncedSearch) {
      setSearchResults(undefined);
      return;
    }

    let isCurrent = true;

    getPreviousAssortmentCandidatesApi(storePogId, debouncedSearch)
      .then((response) => response?.candidates ?? [])
      .catch(() => [])
      .then((candidates) => {
        if (isCurrent) setSearchResults(candidates);
      });

    return () => {
      isCurrent = false;
    };
  }, [storePogId, debouncedSearch]);

  const closeSelector = () => {
    hideAnchorLayer();
    setSearch("");
  };

  const navigateToPreviewPdf = (compareWith: number | null) => {
    window.open(
      routeToUrl(
        pages.merchflowsPogExportPdf_storePogId_,
        { storePogId },
        compareWith === null ? undefined : { compareWith },
      ),
      "_blank",
    );
    closeSelector();
  };

  const isSearching = Boolean(debouncedSearch);
  const isLoading = isSearching ? searchResults === undefined : defaultCandidates === undefined;
  const candidates = (isSearching ? searchResults : defaultCandidates) ?? [];

  return (
    <Flex width="100%" ref={refTrigger}>
      <Button
        width="100%"
        variant="inverted"
        isDisabled={isDisabled}
        isLoading={defaultCandidates === undefined}
        iconRight={{ name: isOpen ? "triangleUp" : "triangleDown" }}
      >
        {t("previewPdf.button")}
      </Button>

      <AnchorLayer
        refLayer={refLayer}
        isRendering={isRendering}
        layerState={layerState}
        isUsingParentWidth
        hideAnchorLayer={closeSelector}
      >
        <DropdownElement
          width="100%"
          onClick={(event: React.SyntheticEvent) => event.stopPropagation()}
        >
          <Search
            search={search}
            setSearch={setSearch}
            placeholder={t("previewPdf.searchPlaceholder")}
            isBorderless
            isFocusedAutomatically
          />

          <DropdownDivider />

          {isLoading ? (
            <Flex padding="10px" justify="center">
              <Spinner size="medium" />
            </Flex>
          ) : (
            <Scroller size="thin" maxHeight="240px" gutter="auto">
              <Flex column role="listbox" aria-label="preview-pdf-candidates">
                {!isSearching && (
                  <Option
                    label={t("previewPdf.mostRecent")}
                    value={null}
                    onSelect={() => navigateToPreviewPdf(null)}
                  />
                )}

                {candidates.map((candidate) => (
                  <Option
                    key={candidate.id}
                    label={candidate.label}
                    value={candidate.id}
                    onSelect={() => navigateToPreviewPdf(candidate.id)}
                  />
                ))}

                {isSearching && candidates.length === 0 && (
                  <Option label={t("previewPdf.noResults")} isSelectable={false} />
                )}
              </Flex>
            </Scroller>
          )}
        </DropdownElement>
      </AnchorLayer>
    </Flex>
  );
};
